import * as React from 'react';
import { IPickSlipLookupProps } from './PickSlipLookup.properties';
import { getPickSlipLookupColumns, getPickSlipLookupSearchColumns, SEARCH_SCREEN_DIALOG_STYLE } from './constants';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';
import BasicLookupField from '@markinson/uicomponents-v2/BasicLookupField';
import { CustomTypesApi } from 'api/customType';
import { WithStyles, withStyles } from '@material-ui/core';
import styles from '../PickSlipLookup/PickSlipLookup.styles';
import { useSearchPickSlipsLookup } from 'api/pickSlips/pickslip';

const PickSlipLookupBatchSize = 100;
const PickSlipLookup = (props: Readonly<IPickSlipLookupProps & WithStyles<typeof styles>>, ref: React.Ref<IBasicLookupFieldHandle>) => {
    const { onSelectedItemChange, onChange, fieldRef, classes, LookupLabel, placeholder, ...restProps } = props;
    const [currentSearchType, setCurrentSearchType] = React.useState<'Lookup' | 'Search'>();
    const usePickSlipLookupMutation = useSearchPickSlipsLookup();
    const [okButtonDisabled, setOkButtonDisabled] = React.useState(false);

    React.useEffect(
        () => {
            setCurrentSearchType('Lookup');
        },
        []
    );

    const retrievePickSlips = React.useCallback(
        async (searchText, searchType, BatchPage?, BatchSize?, Sort?: string) =>
            usePickSlipLookupMutation.mutateAsync
                ({
                    SearchText: searchText || '',
                    Type: searchType || 'Search',
                    BatchPage,
                    BatchSize,
                    Sort
                }),
        []
    );

    const onSearch = React.useCallback(
        async (searchText: string | undefined, batchSize: number, batchPage: number, exactMatch?: boolean): Promise<any> => {
            if (exactMatch) {
                setCurrentSearchType('Lookup');
            } else {
                setCurrentSearchType('Search');
            }
            const data = await retrievePickSlips(searchText, exactMatch ? 'Lookup' : 'Search', batchPage, batchSize)
                .then((response) => response?.PickSlips || []);

            return data;
        },
        []
    );

    const onFindOption = React.useCallback(
        async (searchText: string | undefined): Promise<any> => {
            const data = await CustomTypesApi.lookupGet('PickSlip')(searchText);

            return data;
        },
        []
    );

    const onModuleChange = React.useCallback(
        (moduleId) => {
            setCurrentSearchType(moduleId);
        },
        []
    );

    const handleOnSelectedItemChanged = (selectedItem, searchText) => {
        const pickSlipId = selectedItem?.Details?.PickSlipId ? Number(selectedItem?.Details?.PickSlipId) : selectedItem?.Code;

        if (onChange) {
            onChange(pickSlipId || '');
        }

        if (onSelectedItemChange) {
            onSelectedItemChange(selectedItem, searchText);
        }
    };

    const handleOnSelectionChange = (item: any) => {
        if (item) {
            if (item?.Details?.PickSlipId === 0 && item?.Details?.PickSlipGroup === '') {
                setOkButtonDisabled(true);
            } else {
                setOkButtonDisabled(false);
            }
        }
    };

    const onRowPrepared = (e): void => {
        if (e.rowType === 'data' && e?.data?.Details?.PickSlipId === 0 && e?.data?.Details?.PickSlipGroup === '') {
            e?.rowElement?.classList?.add(classes.inactiveRow);
        }
    };

    return (

        <BasicLookupField
            {...restProps}
            innerRef={fieldRef ? fieldRef : ref}
            valueField={'DespatchId'}
            displayField={'PickSlip'}
            label={LookupLabel ?? 'Pick Slip'}
            descriptionField={'Label'}
            placeholder={placeholder ?? 'Pick Slip'}
            onFindOption={onFindOption}
            batchSize={PickSlipLookupBatchSize}
            lookupOverrides={
                {
                    columns: currentSearchType === 'Lookup' ? getPickSlipLookupColumns() : getPickSlipLookupSearchColumns(),
                    dialogStyle: SEARCH_SCREEN_DIALOG_STYLE,
                    hideColumnHeaders: false,
                    disableGridColumnLines: false
                }
            }
            onSearch={onSearch}
            column1Field={'FormattedValue'}
            column2Field={'Description'}
            isTabbedLookup={true}
            searchScreenTitle={currentSearchType === 'Lookup' ? 'PickSlip Lookup' : 'PickSlip Search'}
            onModuleChange={onModuleChange}
            shouldEnterBehaveAsTab={true}
            onSelectedItemChange={handleOnSelectedItemChanged}
            onSelectionChange={handleOnSelectionChange}
            isOkButtonDisabled={okButtonDisabled}
            dataGridOnRowPrepared={onRowPrepared}
            showSummaryPanel={false}
        />);
};

export default withStyles(styles, { index: 1 })(React.forwardRef(PickSlipLookup));
